import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Button, Spinner, Badge, Alert, Modal, Dropdown } from 'react-bootstrap';
import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { toast } from 'sonner';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import {
    X,
    Trash2,
    RefreshCcw,
    CheckCircle,
    XCircle,
    AlertTriangle,
    ExternalLink,
    Plus,
    Zap,
    Link2,
    MoreVertical,
    Mail,
    AtSign,
    HelpCircle,
    Circle,
    FileText,
} from 'lucide-react';

import {
    getInboxItem,
    dismissInboxItem,
    retryInboxItem,
    approveInboxItem,
    rejectInboxItem,
    reprocessInboxItem,
    generateBrief,
    getMatchCandidates,
    linkSupplierInvoice,
    addBlocklistEntry,
    setInboxItemDone,
} from '../../../../services/InboxService';
import ProviderIcon from '../../Common/ProviderIcon';

// Pull the bare domain out of a sender that may be "Name <addr@dom>" or
// just "addr@dom". Returns '' if there's no usable domain.
function domainFromSender(sender) {
    if (!sender) return '';
    const angle = sender.match(/<([^>]+)>/);
    const addr = (angle ? angle[1] : sender).trim().toLowerCase();
    const at = addr.lastIndexOf('@');
    return at === -1 ? '' : addr.slice(at + 1);
}
function emailFromSender(sender) {
    if (!sender) return '';
    const angle = sender.match(/<([^>]+)>/);
    return (angle ? angle[1] : sender).trim().toLowerCase();
}

// Caret-less kebab trigger for the "manage item" dropdown. react-bootstrap's
// default Dropdown.Toggle renders a caret; a forwardRef custom toggle lets us
// render just the ⋮ icon button (mirrors Midday's MoreVertical trigger).
const KebabToggle = React.forwardRef(function KebabToggle({ onClick, disabled }, ref) {
    return (
        <Button
            ref={ref}
            variant="link"
            size="sm"
            disabled={disabled}
            title="Fler åtgärder"
            style={{ color: '#374151', padding: 4 }}
            onClick={(e) => { e.preventDefault(); onClick(e); }}
        >
            <MoreVertical size={18} />
        </Button>
    );
});

// Display name for the sender: prefer the "Name" part of "Name <addr>",
// otherwise fall back to the bare address.
function senderDisplayName(sender) {
    if (!sender) return '';
    const m = sender.match(/^\s*"?([^"<]+?)"?\s*</);
    if (m && m[1].trim()) return m[1].trim();
    return emailFromSender(sender) || sender;
}
// 1–2 letter initials for the avatar circle.
function initialsOf(name) {
    const parts = (name || '').replace(/[<>"]/g, '').trim().split(/[\s@.]+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * ManageItemMenu — kebab (⋮) dropdown of management actions, mirroring
 * Midday's MoreVertical menu. Lives in the detail header toolbar. The
 * blocklist options are flat items (Blockera e-postadress / Blockera domän),
 * each acting immediately.
 */
function ManageItemMenu({
    disabled, isDone, onToggleDone, canRetry, canReprocess, isTerminal,
    onRetry, onReprocess, onDismiss,
    blockedEmail, blockedDomain, onBlock,
    canManuallyResolve, matchTargets, onCreateInvoice, onOpenMatch,
}) {
    const showBlock = !!blockedEmail;
    const hasUpper = canRetry || canReprocess || !isTerminal;
    return (
        <Dropdown align="end">
            <Dropdown.Toggle as={KebabToggle} disabled={disabled} />
            <Dropdown.Menu>
                {/* Manual "handle it myself" actions sit at the top — they're
                    the highest-intent thing a user does from here. Shown for
                    new / error / manual-awaiting; the agent-HITL Godkänn/Avvisa
                    pair stays in the bottom bar, not here. */}
                {canManuallyResolve && (
                    <>
                        <Dropdown.Item onClick={onCreateInvoice}>
                            <Plus size={14} className="me-2" />Skapa faktura
                        </Dropdown.Item>
                        {matchTargets.map((target) => (
                            <Dropdown.Item key={target.key} onClick={() => onOpenMatch(target)}>
                                <Link2 size={14} className="me-2" />{target.buttonLabel}
                            </Dropdown.Item>
                        ))}
                        <Dropdown.Divider />
                    </>
                )}
                <Dropdown.Item onClick={onToggleDone}>
                    {isDone
                        ? <><Circle size={14} className="me-2" />Markera som ej klar</>
                        : <><CheckCircle size={14} className="me-2" />Markera som klar</>}
                </Dropdown.Item>
                {(hasUpper || showBlock) && <Dropdown.Divider />}
                {canRetry && (
                    <Dropdown.Item onClick={onRetry}>
                        <RefreshCcw size={14} className="me-2" />Försök igen
                    </Dropdown.Item>
                )}
                {canReprocess && (
                    <Dropdown.Item onClick={onReprocess}>
                        <Zap size={14} className="me-2" />Försök igen med AI
                    </Dropdown.Item>
                )}
                {!isTerminal && (
                    <Dropdown.Item onClick={onDismiss}>
                        <Trash2 size={14} className="me-2" />Avfärda
                    </Dropdown.Item>
                )}
                {showBlock && hasUpper && <Dropdown.Divider />}
                {showBlock && (
                    <Dropdown.Item onClick={() => onBlock('email')} title={blockedEmail}>
                        <Mail size={14} className="me-2" />Blockera e-postadress
                    </Dropdown.Item>
                )}
                {showBlock && blockedDomain && (
                    <Dropdown.Item onClick={() => onBlock('domain')} title={`@${blockedDomain}`}>
                        <AtSign size={14} className="me-2" />Blockera domän
                    </Dropdown.Item>
                )}
            </Dropdown.Menu>
        </Dropdown>
    );
}

/**
 * DocumentOverlay — a small "?" button floating at the bottom-right of the
 * document. Hovering reveals a popover with the details (extracted fields, or
 * the brief content when the agent produced one). When nothing's been
 * generated yet, clicking the button generates it on demand. Renders nothing
 * if there's neither content nor the ability to generate.
 */
function DocumentOverlay({ brief, ocrFields, canGenerate, onGenerate, generating }) {
    const [open, setOpen] = useState(false);
    const closeTimer = useRef(null);
    const hasDetails = !!brief || !!ocrFields;
    if (!hasDetails && !canGenerate) return null;

    const openNow = () => { if (closeTimer.current) clearTimeout(closeTimer.current); setOpen(true); };
    const closeSoon = () => { closeTimer.current = setTimeout(() => setOpen(false), 150); };

    const handleClick = () => {
        // Smart click: nothing generated yet → generate it. Otherwise the
        // hover already shows the details, so a click just pins them open.
        if (!brief && canGenerate && !generating) {
            onGenerate();
            return;
        }
        setOpen((o) => !o);
    };

    return (
        <div
            style={{ position: 'absolute', right: 16, bottom: 16, zIndex: 30 }}
            onMouseEnter={openNow}
            onMouseLeave={closeSoon}
        >
            {open && (
                <div style={{
                    position: 'absolute', bottom: 'calc(100% + 8px)', right: 0,
                    width: 340, maxHeight: '60vh', overflow: 'auto',
                    background: 'rgba(255,255,255,0.98)', border: '1px solid #e5e7eb',
                    borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
                    backdropFilter: 'blur(6px)', padding: 12,
                }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                        Detaljer
                    </div>
                    {hasDetails ? (
                        brief ? <LLMBrief brief={brief} /> : <OcrFieldsSummary fields={ocrFields} />
                    ) : (
                        <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                            Klicka för att hämta detaljer från dokumentet.
                        </p>
                    )}
                </div>
            )}
            <button
                type="button"
                onClick={handleClick}
                title={hasDetails ? 'Visa detaljer' : 'Hämta detaljer'}
                style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: '#fff', border: '1px solid #e5e7eb',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.16)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: '#374151', padding: 0,
                }}
            >
                {generating ? <Spinner animation="border" size="sm" /> : <HelpCircle size={20} />}
            </button>
        </div>
    );
}

/**
 * InboxDetail — right-pane detail view for the currently selected inbox item.
 *
 * Layout (Midday-style):
 *   1. Toolbar — state badge, source icon, ⋮ manage menu, close
 *   2. Metadata header — avatar, sender, subject, amount, date
 *   3. Document — full-width pdf.js render; AI brief / fields float over it
 *   4. Primary action bar — Godkänn/Avvisa or Matcha/Skapa faktura
 *
 * Mutation pattern: every action calls `onMutated()` after a successful
 * API call so the parent list and the sidebar badge refresh. Errors
 * show a toast and leave UI state unchanged.
 */

const STATE_BADGE = {
    new: { variant: 'primary', label: 'Ny' },
    processing: { variant: 'info', label: 'Bearbetas' },
    awaiting_approval: { variant: 'warning', label: 'Väntar godkännande' },
    linked: { variant: 'success', label: 'Hanterad' },
    dismissed: { variant: 'secondary', label: 'Avfärdad' },
    error: { variant: 'danger', label: 'Fel' },
};

const SOURCE_LABEL = {
    gmail: 'Gmail',
    outlook: 'Outlook',
    mailgun: 'E-postvidarebefordran',
    upload: 'Uppladdning',
};

function formatDateTime(value) {
    if (!value) return '—';
    const d = dayjs(value);
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : value;
}

/**
 * Match-target descriptors — the kinds of existing record an inbox item can
 * be linked to ("Matcha mot befintlig …"). Each descriptor fully owns its
 * data shape so the generic match modal + action bar stay target-agnostic:
 *
 *   key            stable id (used for React keys / button keys)
 *   buttonLabel    label on the action-bar button
 *   modalTitle     title of the candidates modal
 *   emptyText      shown when the matcher returns no candidates
 *   fetchCandidates(companyId, itemId) -> Promise<[{ score, ...record }]>
 *   link(companyId, itemId, recordId)  -> Promise  (atomically links + transitions)
 *   getRecordId(candidate)             -> id passed back to link()
 *   recordHref(candidate, companyId)   -> deep-link to the record (or null)
 *   renderSummary(candidate)           -> JSX for the candidate row body
 *
 * Adding a second target later (e.g. bank transactions) is purely additive:
 * append a descriptor here, point it at its own candidate/link endpoints, and
 * the button + modal render it with no further changes. The backend mirrors
 * this — `linked` is the shared terminal state regardless of target type.
 */
const MATCH_TARGETS = [
    {
        key: 'supplier_invoice',
        buttonLabel: 'Matcha mot befintlig',
        modalTitle: 'Matcha mot befintlig faktura',
        emptyText: 'Hittade inga möjliga matchningar. Skapa en ny faktura istället.',
        fetchCandidates: async (companyId, itemId) => {
            const { candidates } = await getMatchCandidates(companyId, itemId);
            return candidates || [];
        },
        link: (companyId, itemId, recordId) => linkSupplierInvoice(companyId, itemId, recordId),
        getRecordId: (c) => c.supplier_invoice.invoice_id,
        recordHref: (c, companyId) => `/${companyId}/supplier-invoices/${c.supplier_invoice.invoice_id}`,
        renderSummary: (c) => {
            const si = c.supplier_invoice;
            return (
                <>
                    <div style={{ fontWeight: 500 }}>
                        Faktura #{si.invoice_number || '(saknas)'} — {si.amount} SEK
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                        Datum: {si.invoice_date} · Status: {si.status}
                        {si.due_date && ` · Förfaller: ${si.due_date}`}
                    </div>
                </>
            );
        },
    },
];

export default function InboxDetail({ companyId, item: itemFromList, onClose, onMutated }) {
    // The list row has enough for v1, but re-fetch on selection to pick up
    // the full llm_brief / attachment_ids that the list query doesn't expand.
    const [item, setItem] = useState(itemFromList);
    // We don't currently show a spinner while the full item re-fetches
    // because the list-row data is enough to render the header + state
    // immediately; the re-fetch just upgrades the body. If users report
    // missing-feedback we can wire this back in.
    const [, setIsLoading] = useState(true);
    const [actionInFlight, setActionInFlight] = useState(null);

    // Document preview — fetch EVERY attachment as a blob URL so a multi-
    // attachment email shows all of them (switchable), not just the first.
    const [docAttachments, setDocAttachments] = useState([]); // [{ id, url?, error? }]
    const [activeAttIdx, setActiveAttIdx] = useState(0);
    const blobUrlsRef = useRef([]); // for cleanup

    const itemId = itemFromList.id;
    const history = useHistory();

    const refresh = useCallback(async () => {
        try {
            setIsLoading(true);
            const full = await getInboxItem(companyId, itemId);
            setItem(full);
        } catch (err) {
            toast.error(err?.response?.data?.error || err.message || 'Kunde inte ladda objektet');
        } finally {
            setIsLoading(false);
        }
    }, [companyId, itemId]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Fetch all attachments on attachment_ids change. We use the same
    // /attachments/:id endpoint that ViewSupplierInvoice uses, served as a
    // blob so the user's session cookie authenticates the request. Results
    // stream in (the first shows as soon as it's ready); per-attachment
    // failures are captured so one bad blob doesn't blank the others.
    const attKey = Array.isArray(item?.attachment_ids) ? item.attachment_ids.join(',') : '';
    useEffect(() => {
        const ids = Array.isArray(item?.attachment_ids) ? item.attachment_ids : [];
        blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
        blobUrlsRef.current = [];
        setActiveAttIdx(0);
        setDocAttachments([]);
        if (ids.length === 0) return undefined;
        let cancelled = false;
        (async () => {
            const results = [];
            for (const id of ids) {
                try {
                    const url = getApiUrl(`${companyId}/attachments/${id}`);
                    const resp = await axios.get(url, { withCredentials: true, responseType: 'blob' });
                    if (cancelled) return;
                    const blobUrl = URL.createObjectURL(resp.data);
                    blobUrlsRef.current.push(blobUrl);
                    results.push({ id, url: blobUrl });
                } catch (err) {
                    if (cancelled) return;
                    results.push({ id, error: err?.response?.data?.error || err.message || 'Kunde inte ladda bilagan' });
                }
                if (!cancelled) setDocAttachments([...results]);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, attKey]);

    // Cleanup blob URLs on unmount.
    useEffect(() => () => {
        blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    }, []);

    const runAction = useCallback(
        async (label, fn) => {
            setActionInFlight(label);
            try {
                const result = await fn();
                toast.success(result?.message || `${label} klart`);
                await refresh();
                onMutated?.();
            } catch (err) {
                const msg = err?.response?.data?.error || err.message || `${label} misslyckades`;
                toast.error(msg);
            } finally {
                setActionInFlight(null);
            }
        },
        [refresh, onMutated],
    );

    const onDismiss = () =>
        runAction('Avfärda', () => dismissInboxItem(companyId, itemId, { reason: null }));
    const onRetry = () => runAction('Försök igen', () => retryInboxItem(companyId, itemId));
    const onApprove = () => runAction('Godkänn', () => approveInboxItem(companyId, itemId));
    const onReject = () => runAction('Avvisa', () => rejectInboxItem(companyId, itemId));
    const onReprocess = () =>
        runAction('Försök igen med AI', () => reprocessInboxItem(companyId, itemId));

    // Match-to-existing flow: open a modal listing ranked candidate records
    // returned by the backend matcher, scored high/medium/low. Loaded on
    // demand (one request when the modal opens), not on every detail render —
    // the query is free but the response is a few rows we don't need until
    // the user asks.
    //
    // The flow is driven by a *match-target descriptor* (see MATCH_TARGETS):
    // each describes one kind of record an inbox item can be linked to. Today
    // there is exactly one — existing supplier invoices. A second target
    // (e.g. bank transactions) slots in by adding a descriptor + its
    // candidate/link endpoints; nothing here needs to change. `matchTarget`
    // holds the descriptor whose modal is open (null = closed).
    const [matchTarget, setMatchTarget] = useState(null);
    const [matchCandidates, setMatchCandidates] = useState(null); // null = not loaded
    const [matchLoading, setMatchLoading] = useState(false);

    const openMatchModal = useCallback(async (target) => {
        setMatchTarget(target);
        setMatchCandidates(null);
        setMatchLoading(true);
        try {
            const candidates = await target.fetchCandidates(companyId, itemId);
            setMatchCandidates(candidates || []);
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Kunde inte hämta matchningar');
            setMatchCandidates([]);
        } finally {
            setMatchLoading(false);
        }
    }, [companyId, itemId]);

    const closeMatchModal = useCallback(() => {
        setMatchTarget(null);
    }, []);

    const onPickMatch = useCallback(
        (recordId) => runAction(
            'Länka till befintlig',
            async () => {
                const r = await matchTarget.link(companyId, itemId, recordId);
                closeMatchModal();
                return r;
            },
        ),
        [runAction, companyId, itemId, closeMatchModal, matchTarget],
    );
    // Block-sender flow (mirrors Midday's Blocklist submenu): two distinct
    // actions — block the exact address or the whole domain — that act
    // immediately, no intermediate dialog. Adding the rule only affects
    // future intake, so we also dismiss the current item (unless terminal).
    const blockedEmail = emailFromSender(item?.sender_email);
    const blockedDomain = domainFromSender(item?.sender_email);

    const onBlock = useCallback(
        (matchType) =>
            runAction('Blockera avsändaren', async () => {
                const value = matchType === 'email'
                    ? emailFromSender(item?.sender_email)
                    : domainFromSender(item?.sender_email);
                if (!value) throw new Error('Kunde inte tolka avsändaren');
                // Adding the rule also retroactively hides every non-terminal
                // item from this sender (incl. the current one), so we don't
                // dismiss it separately here.
                const res = await addBlocklistEntry(companyId, { matchType, value });
                const base = matchType === 'email' ? 'E-postadressen blockerad' : 'Domänen blockerad';
                const hidden = res?.hidden || 0;
                return { message: hidden > 0 ? `${base} — ${hidden} objekt dolda` : base };
            }),
        [runAction, companyId, item?.sender_email],
    );

    // Toggle the reversible "mark as done" flag.
    const onToggleDone = useCallback(
        () => runAction(
            item?.done_at ? 'Markera som ej klar' : 'Markera som klar',
            () => setInboxItemDone(companyId, itemId, !item?.done_at),
        ),
        [runAction, companyId, itemId, item?.done_at],
    );

    // Generate the document brief on demand. Triggered "smartly" by clicking
    // the ? overlay when nothing's been generated yet. Neutral label.
    const onGenerate = useCallback(
        () => runAction('Hämta detaljer', () => generateBrief(companyId, itemId, { entityType: 'inbox_item' })),
        [runAction, companyId, itemId],
    );

    const onCreateInvoice = () => {
        // Deep-link to CreateSupplierInvoice with the inbox_item id; the
        // form's mount-effect picks up `?from_inbox_item=…` and prefills
        // from ocr_fields. After save, that page calls linkSupplierInvoice
        // to mark this item linked.
        history.push(`/${companyId}/create-supplier-invoices?from_inbox_item=${encodeURIComponent(itemId)}`);
    };

    const stateCfg = STATE_BADGE[item?.state] || { variant: 'secondary', label: item?.state };
    const isTerminal = item?.state === 'linked' || item?.state === 'dismissed';

    // Awaiting-approval breaks into two sub-cases based on whether the
    // agent paused the run (HITL) or no agent ever ran (manual tier /
    // credit-gated skip). The signal is agent_thread_id: agents that
    // started always populate it.
    const isAgentAwaiting = item?.state === 'awaiting_approval' && !!item?.agent_thread_id;
    const isManualAwaiting = item?.state === 'awaiting_approval' && !item?.agent_thread_id;

    const canApproveViaAgent = isAgentAwaiting;
    // "I'll book/match it myself" — available wherever the user owns the
    // decision: a fresh item (new), one that failed the pipeline (error),
    // or a manual-tier item parked for approval with no agent behind it.
    // Deliberately NOT shown while `processing` (the agent owns the item and
    // a manual link would race its callback) nor for agent-HITL
    // awaiting_approval (that has Godkänn/Avvisa; a manual link would orphan
    // the paused agent run). The backend state machine allows new→linked and
    // error→linked to back this; processing→linked stays agent-only.
    const canManuallyResolve =
        item?.state === 'new' || item?.state === 'error' || isManualAwaiting;
    const canRetry = item?.state === 'error';
    const canReprocess = !!item?.ai_skipped_reason && !isTerminal;

    return (
        <div style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 120px)',
            minHeight: 560,
            overflow: 'hidden',
        }}>
            {/* Strip @react-pdf-viewer's default page padding / margin /
                shadow so the document renders edge-to-edge (no side strips). */}
            <style>{`
                .inbox-doc-pdf .rpv-core__inner-pages { padding: 0 !important; }
                .inbox-doc-pdf .rpv-core__inner-page { padding: 0 !important; background: transparent !important; }
                .inbox-doc-pdf .rpv-core__page-layer { margin: 0 auto !important; box-shadow: none !important; }
                .inbox-doc-pdf .rpv-core__canvas-layer canvas { width: 100% !important; height: auto !important; }
            `}</style>
            {/* Toolbar — state badge on the left; source, manage-menu (⋮)
                and close on the right (mirrors Midday's top toolbar, with
                the management dropdown moved up here). */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                borderBottom: '1px solid #e5e7eb',
            }}>
                <Badge bg={stateCfg.variant}>{stateCfg.label}</Badge>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center' }} title={SOURCE_LABEL[item?.source] || item?.source}>
                        <ProviderIcon provider={item?.source} size={16} />
                    </span>
                    <span style={{ width: 1, height: 20, background: '#e5e7eb' }} />
                    <ManageItemMenu
                        disabled={!!actionInFlight}
                        isDone={!!item?.done_at}
                        onToggleDone={onToggleDone}
                        canRetry={canRetry}
                        canReprocess={canReprocess}
                        isTerminal={isTerminal}
                        onRetry={onRetry}
                        onReprocess={onReprocess}
                        onDismiss={onDismiss}
                        blockedEmail={blockedEmail}
                        blockedDomain={blockedDomain}
                        onBlock={onBlock}
                        canManuallyResolve={canManuallyResolve}
                        matchTargets={MATCH_TARGETS}
                        onCreateInvoice={onCreateInvoice}
                        onOpenMatch={openMatchModal}
                    />
                    <span style={{ width: 1, height: 20, background: '#e5e7eb' }} />
                    <Button variant="link" onClick={onClose} title="Stäng" style={{ padding: 4, color: '#374151' }}>
                        <X size={18} />
                    </Button>
                </div>
            </div>

            {/* Metadata — avatar + sender + subject on the left, date on the
                right (Midday's display-name / amount / date header). */}
            <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: 16,
                borderBottom: '1px solid #e5e7eb',
            }}>
                <div style={{
                    width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                    background: 'var(--rgba-primary-1)', color: 'var(--primary)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 600,
                }}>
                    {initialsOf(senderDisplayName(item?.sender_email))}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={item?.sender_email || ''}>
                        {senderDisplayName(item?.sender_email) || '—'}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={item?.subject || ''}>
                        {item?.subject || '(inget ämne)'}
                    </div>
                    {item?.ocr_fields?.totalAmount != null && (
                        <div style={{ fontSize: 13, marginTop: 2 }}>
                            {item.ocr_fields.totalAmount} {item.ocr_fields.currency || 'SEK'}
                        </div>
                    )}
                </div>
                <div style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: 12, whiteSpace: 'nowrap' }}>
                    {formatDateTime(item?.received_at)}
                </div>
            </div>

            {/* Status banners — a thin strip above the document so they
                don't cover it. Rare (error / credit-gate / linked). */}
            {((item?.state === 'error' && item?.error_message) || (item?.ai_skipped_reason && !isTerminal) || item?.supplier_invoice_id) && (
                <div style={{ padding: '8px 12px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {item?.state === 'error' && item?.error_message && (
                        <Alert variant="danger" className="mb-0" style={{ fontSize: 13 }}>
                            <AlertTriangle size={14} className="me-2" />
                            <strong>{item.error_code || 'Fel'}:</strong> {item.error_message}
                        </Alert>
                    )}
                    {item?.ai_skipped_reason && !isTerminal && (
                        <Alert variant="warning" className="mb-0" style={{ fontSize: 13 }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                <Zap size={16} style={{ marginTop: 2, flexShrink: 0 }} />
                                <div style={{ flex: 1 }}>
                                    <strong>{aiSkippedReasonTitle(item.ai_skipped_reason)}</strong>
                                    <p style={{ margin: '4px 0 0', fontSize: 12 }}>
                                        {aiSkippedReasonHint(item.ai_skipped_reason)}
                                    </p>
                                </div>
                            </div>
                        </Alert>
                    )}
                    {item?.supplier_invoice_id && (
                        <Alert variant="success" className="mb-0" style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <CheckCircle size={16} />
                            <span>Bokförd som leverantörsfaktura.</span>
                            <a
                                href={`/${companyId}/supplier-invoices/${item.supplier_invoice_id}`}
                                style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            >
                                Öppna <ExternalLink size={12} />
                            </a>
                        </Alert>
                    )}
                </div>
            )}

            {/* Document area — fills the pane at full width. pdf.js renders
                the pages directly (no browser PDF chrome). When the email had
                more than one attachment, a switcher lets the user flip between
                them. The AI summary / extracted fields float as a collapsible
                overlay over the bottom, like Midday's transaction overlay. */}
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: '#f3f4f6' }}>
                {docAttachments.length > 1 && (
                    <div style={{ display: 'flex', gap: 6, padding: '8px 12px', borderBottom: '1px solid #e5e7eb', background: '#fff', flexWrap: 'wrap' }}>
                        {docAttachments.map((a, i) => {
                            const isActive = i === activeAttIdx;
                            return (
                                <button
                                    key={a.id}
                                    type="button"
                                    onClick={() => setActiveAttIdx(i)}
                                    style={{
                                        border: isActive ? '1px solid var(--primary)' : '1px solid #e5e7eb',
                                        background: isActive ? 'var(--rgba-primary-1)' : '#fff',
                                        color: isActive ? 'var(--primary)' : '#374151',
                                        padding: '4px 12px',
                                        borderRadius: 999,
                                        fontSize: 12,
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                    }}
                                >
                                    <FileText size={12} />
                                    Bilaga {i + 1}
                                    {a.error && <span title={a.error} style={{ color: '#dc3545' }}>!</span>}
                                </button>
                            );
                        })}
                    </div>
                )}
                <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
                    {(() => {
                        const active = docAttachments[activeAttIdx] || null;
                        if (active?.error) {
                            return (
                                <div style={{ padding: 16 }}>
                                    <Alert variant="warning" className="mb-0" style={{ fontSize: 13 }}>{active.error}</Alert>
                                </div>
                            );
                        }
                        if (active?.url) {
                            return (
                                <div className="inbox-doc-pdf" style={{ position: 'absolute', inset: 0 }}>
                                    <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js">
                                        <Viewer key={active.id} fileUrl={active.url} defaultScale={SpecialZoomLevel.PageWidth} />
                                    </Worker>
                                </div>
                            );
                        }
                        if (Array.isArray(item?.attachment_ids) && item.attachment_ids.length > 0) {
                            return (
                                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
                                    <Spinner animation="border" size="sm" /> <span className="ms-2">Laddar förhandsvisning…</span>
                                </div>
                            );
                        }
                        return (
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13 }}>
                                Ingen bilaga kopplad till detta objekt.
                            </div>
                        );
                    })()}

                    <DocumentOverlay
                        brief={item?.llm_brief}
                        ocrFields={(!item?.supplier_invoice_id && item?.ocr_fields) ? item.ocr_fields : null}
                        canGenerate={!isTerminal && !item?.llm_brief}
                        onGenerate={onGenerate}
                        generating={actionInFlight === 'Hämta detaljer'}
                    />
                </div>
            </div>

            {/* Bottom bar is reserved for the agent-HITL decision (Godkänn /
                Avvisa) — the one action that must be unmissable. Everything
                else, including the manual "Skapa faktura" / "Matcha mot
                befintlig" path, lives in the ⋮ menu in the top toolbar. The
                row is hidden entirely when there's no agent decision pending. */}
            {canApproveViaAgent && (
                <div style={{
                    borderTop: '1px solid #e5e7eb',
                    padding: 12,
                    display: 'flex',
                    gap: 8,
                    background: '#f9fafb',
                    flexWrap: 'wrap',
                }}>
                    <Button variant="success" size="sm" onClick={onApprove} disabled={!!actionInFlight}>
                        {actionInFlight === 'Godkänn' ? <Spinner animation="border" size="sm" /> : <CheckCircle size={14} />}
                        <span className="ms-1">Godkänn</span>
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={onReject} disabled={!!actionInFlight}>
                        {actionInFlight === 'Avvisa' ? <Spinner animation="border" size="sm" /> : <XCircle size={14} />}
                        <span className="ms-1">Avvisa</span>
                    </Button>
                </div>
            )}

            <MatchCandidatesModal
                show={!!matchTarget}
                target={matchTarget}
                onClose={closeMatchModal}
                loading={matchLoading}
                candidates={matchCandidates}
                onPick={onPickMatch}
                actionInFlight={actionInFlight}
                companyId={companyId}
            />
        </div>
    );
}

// Score → badge styling. Generic to the matching concept (any target type
// scores candidates high/medium/low), so it lives on the modal, not the
// per-target descriptor.
const SCORE_META = {
    high:   { variant: 'warning', label: 'Hög träff'   },
    medium: { variant: 'info',    label: 'Medel träff' },
    low:    { variant: 'light',   label: 'Låg träff'   },
};

/**
 * Modal listing ranked candidate records returned by the backend matcher.
 * Target-agnostic: the open match-target descriptor (`target`) supplies the
 * title, empty-state text, and how to render / identify / deep-link each
 * candidate. Each candidate carries a `score` (high/medium/low) surfaced as
 * a colored badge so the user can tell certain matches from speculative ones.
 * "Länka" picks a candidate; the parent links it and closes on success.
 */
function MatchCandidatesModal({ show, target, onClose, loading, candidates, onPick, actionInFlight, companyId }) {
    return (
        <Modal show={show} onHide={onClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 16 }}>{target?.modalTitle || 'Matcha mot befintlig'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading && (
                    <div style={{ textAlign: 'center', padding: 24 }}>
                        <Spinner animation="border" size="sm" /> <span className="ms-2">Söker matchningar…</span>
                    </div>
                )}
                {!loading && candidates && candidates.length === 0 && (
                    <Alert variant="light" className="mb-0">
                        {target?.emptyText || 'Hittade inga möjliga matchningar.'}
                    </Alert>
                )}
                {!loading && target && candidates && candidates.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {candidates.map((c) => {
                            const meta = SCORE_META[c.score] || SCORE_META.low;
                            const recordId = target.getRecordId(c);
                            const href = target.recordHref(c, companyId);
                            return (
                                <div key={recordId} style={{
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 8,
                                    padding: 12,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                }}>
                                    <Badge bg={meta.variant} text={meta.variant === 'light' ? 'dark' : undefined}>{meta.label}</Badge>
                                    <div style={{ flex: 1 }}>
                                        {target.renderSummary(c)}
                                    </div>
                                    {href && (
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            href={href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <ExternalLink size={12} />
                                        </Button>
                                    )}
                                    <Button
                                        size="sm"
                                        variant="primary"
                                        onClick={() => onPick(recordId)}
                                        disabled={!!actionInFlight}
                                    >
                                        {actionInFlight === 'Länka till befintlig' ? <Spinner animation="border" size="sm" /> : 'Länka'}
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
}

// User-friendly Swedish text for each structured ai_skipped_reason. The
// banner is the user's first signal that they need to take a billing
// action — so it must explain (in plain language) what happened and what
// to do about it.
function aiSkippedReasonTitle(reason) {
    switch (reason) {
        case 'no_ocr_credits':   return 'AI-fakturatolkning skippades — krediter slut';
        case 'no_agent_credits': return 'AI-bokföringsförslag skippades — krediter slut';
        case 'feature_disabled': return 'AI-funktioner inaktiverade på din plan';
        case 'user_opt_out':     return 'AI-bearbetning avstängd för det här objektet';
        default:                 return 'AI-bearbetning utfördes inte';
    }
}
function aiSkippedReasonHint(reason) {
    switch (reason) {
        case 'no_ocr_credits':
            return 'Lägg till AI-krediter för att låta systemet läsa av fakturan automatiskt, eller skapa fakturan manuellt nedan.';
        case 'no_agent_credits':
            return 'Fälten är extraherade — klicka "Skapa faktura" för att gå vidare, eller köp fler krediter för automatisk hantering.';
        case 'feature_disabled':
            return 'Uppgradera planen om du vill att fakturor ska AI-bearbetas automatiskt.';
        case 'user_opt_out':
            return 'Du har själv valt att skippa AI för det här objektet.';
        default:
            return null;
    }
}

// Compact read-out of the OCR-extracted invoice fields. Two-column
// label/value grid. Used when the user is about to click "Skapa faktura"
// and wants to sanity-check what OCR pulled out.
function OcrFieldsSummary({ fields }) {
    if (!fields) return null;
    const rows = [
        ['Leverantör',     fields.supplierName],
        ['Org.nr',         fields.supplierOrgNumber],
        ['Fakturanummer',  fields.invoiceNumber],
        ['Fakturadatum',   fields.invoiceDate],
        ['Förfallodatum',  fields.dueDate],
        ['Totalt',         fields.totalAmount != null ? `${fields.totalAmount} ${fields.currency || 'SEK'}` : null],
        ['Moms',           fields.vatAmount],
        ['Bankgiro',       fields.bankgiro],
        ['IBAN',           fields.iban],
        ['OCR-referens',   fields.ocrReference],
    ].filter(([, v]) => v != null && v !== '');

    if (rows.length === 0) {
        return <p style={{ margin: 0, color: '#9ca3af', fontSize: 13 }}>Inga fält extraherade.</p>;
    }
    return (
        <dl style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '4px 12px',
            margin: 0,
            fontSize: 13,
        }}>
            {rows.map(([label, value]) => (
                <React.Fragment key={label}>
                    <dt style={{ color: '#6b7280', fontWeight: 500 }}>{label}</dt>
                    <dd style={{ margin: 0 }}>{String(value)}</dd>
                </React.Fragment>
            ))}
        </dl>
    );
}

function LLMBrief({ brief }) {
    if (!brief || typeof brief !== 'object') return null;
    // Missing invoice_likelihood = legacy brief from before classification
    // was added; treat as confident so old briefs render unchanged.
    const likelihood = brief.invoice_likelihood || 'yes';
    const notConfident = likelihood !== 'yes';
    // Wash out the warm yellow when the model says this probably isn't an
    // invoice — the user shouldn't read a confident BAS suggestion off it.
    const bg = notConfident ? '#f3f4f6' : '#fefce8';
    const border = notConfident ? '#d1d5db' : '#fde68a';
    return (
        <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 8, padding: 12, fontSize: 13 }}>
            {notConfident && (
                <div style={{ marginBottom: 8 }}>
                    <Badge bg={likelihood === 'no' ? 'secondary' : 'warning'}>
                        {likelihood === 'no' && 'Verkar inte vara en faktura'}
                        {likelihood === 'unclear' && 'Osäker faktura-klassificering'}
                        {likelihood === 'probably' && 'Troligen en faktura (något fält saknas)'}
                    </Badge>
                </div>
            )}
            {brief.summary_sv && <p style={{ marginBottom: 8 }}>{brief.summary_sv}</p>}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 12 }}>
                {brief.suggested_bas_account && (
                    <Badge bg="light" text="dark" style={{ border: '1px solid #e5e7eb' }}>
                        Föreslaget konto: <strong>{brief.suggested_bas_account}</strong>
                    </Badge>
                )}
                {brief.recurring && (
                    <Badge bg="info">Återkommande</Badge>
                )}
                {Array.isArray(brief.anomalies) && brief.anomalies.length > 0 && (
                    <Badge bg="warning">{brief.anomalies.length} avvikelse(r)</Badge>
                )}
            </div>
            {Array.isArray(brief.anomalies) && brief.anomalies.length > 0 && (
                <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 18, color: '#92400e' }}>
                    {brief.anomalies.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
            )}
        </div>
    );
}
